import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  ExternalLink,
  Eye,
  Info,
  Layers3,
  RefreshCw,
  Route,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { MemberDataState } from '../components/DataState';
import { MatrixGraph } from '../components/MatrixGraph';
import { POSITION_KIND_LABEL } from '../components/PositionGrid';
import { ProjectedSlotPanel } from '../components/SlotMatrix';
import { PageHeading, StatusPill } from '../components/Ui';
import { formatToken, shortenAddress } from '../lib/format';
import { EXPLORER_URL } from '../lib/network';
import { isSlotKey, isSubSlot, previousSlot, slotLabel } from '../lib/slots';
import { atomicToDecimal, useAppData } from '../state/AppDataContext';

/*
 * One slot of the member's own journey. Owner 2026-10-11: everything here is what the contract did - the grid is the
 * API's on-chain slot view (indexed SeatFilled events), the cycle list and the routing ledger are the on-chain cycles
 * (/api/me/cycles, built from the same seats). A slot the contract has not activated is shown as locked.
 */
export function SlotDetailPage() {
  const data = useAppData();
  const params = useParams();
  const slotNumber = Number(params.slotId);
  const slot = data.slots.find((candidate) => candidate.number === slotNumber);
  const [cycle, setCycle] = useState(1);
  const slotCycles = useMemo(() => data.cycles.filter((candidate) => candidate.slotNumber === slotNumber).sort((a, b) => a.cycleNumber - b.cycleNumber), [data.cycles, slotNumber]);
  const nodes = useMemo(() => data.matrixForCycle(slotNumber, cycle), [cycle, data, slotNumber]);
  const selectedCycle = slotCycles.find((candidate) => candidate.cycleNumber === cycle);
  const slotView = data.projectedSlots.find((candidate) => candidate.slotNumber === slotNumber) ?? null;
  // The slot's own current cycle counts towards the maximum: without it a slot sitting on cycle 3
  // with no indexed cycle rows rendered the impossible "Cycle 3 of 1".
  const maxCycle = Math.max(1, slot?.cycle ?? 1, slotView?.currentCycleNumber ?? 1, ...slotCycles.map((candidate) => candidate.cycleNumber));

  useEffect(() => {
    if (slot?.active) setCycle(Math.max(1, slot.cycle));
  }, [slot?.active, slot?.cycle]);

  if (!data.member) return <MemberDataState mode={data.mode} error={data.error} onRetry={data.refresh} resource="slot data" />;
  // 1..16 plus the Half / Quarter sub-slots (50 = Slot ½, 25 = Slot ¼).
  if (!slot || !isSlotKey(slotNumber)) return <Navigate to="/slots" replace />;
  const member = data.member;
  const previous = previousSlot(slot.number);
  const placements = selectedCycle?.placements ?? [];
  const directSeats = placements.filter((placement) => placement.placementType === 'direct').length;

  return (
    <>
      <Link to="/slots" className="back-link"><ArrowLeft size={15} /> All slots</Link>
      <PageHeading
        eyebrow={isSubSlot(slot.number) ? `ERA SUB-SLOT ${slotLabel(slot.number)}${slot.virtual ? ' · VIRTUAL' : ''}` : `MATRIX LEVEL ${slot.number.toString().padStart(2, '0')}`}
        title={slot.label}
        description="Twelve positions per cycle, each seat read from the contract's indexed SeatFilled events."
        actions={<><StatusPill tone={slot.active ? 'green' : 'amber'}>{slot.active ? 'Active on-chain' : 'Locked'}</StatusPill><span className="slot-detail-price">{formatToken(slot.price)}</span></>}
      />

      {!slot.active ? (
        <section className="panel locked-detail">
          <span className="large-lock"><ShieldCheck /></span><span className="eyebrow">PROGRESSION GUARD</span><h2>{slot.label} is not yet active</h2><p>The contract has not activated this slot for {member.id}. It opens on-chain when cycle 1 P3 + P4 of the previous slot are filled and the ID has its qualifying directs.</p>{previous !== null && data.slots.some((candidate) => candidate.number === previous) ? <Link to={`/slots/${previous}`} className="button primary">View previous slot <ArrowRight size={16} /></Link> : null}
        </section>
      ) : (
        <>
          <section className="matrix-header panel">
            <div><span className="eyebrow">ACTIVE ID</span><strong>{member.id}</strong><small>{shortenAddress(member.wallet, 6)}</small></div>
            <div className="cycle-control">
              <button type="button" onClick={() => setCycle((value) => Math.max(1, value - 1))} disabled={cycle <= 1} aria-label="Previous cycle"><ChevronLeft /></button>
              <span><small>VIEWING</small><strong>Cycle {cycle} <em>of {maxCycle}</em></strong></span>
              <button type="button" onClick={() => setCycle((value) => Math.min(maxCycle, value + 1))} disabled={cycle >= maxCycle} aria-label="Next cycle"><ChevronRight /></button>
            </div>
            <div className="matrix-state"><span className="pulse-ring"><i /></span><div><small>CHAIN STATE</small><strong>{selectedCycle ? `${selectedCycle.completedPositions} of 12 seats filled on-chain` : 'No seat indexed in this cycle yet'}</strong></div></div>
          </section>

          <AnimatePresence mode="wait">
            <motion.section key={cycle} className="panel matrix-panel" initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -18 }} transition={{ duration: 0.3 }}>
              <div className="matrix-panel-heading"><div><span className="eyebrow">ON-CHAIN PLACEMENT MAP</span><h2>Cycle {cycle} topology</h2></div><div><StatusPill tone={selectedCycle?.status === 'completed' ? 'green' : selectedCycle ? 'violet' : 'amber'}>{selectedCycle?.status ?? 'No seats yet'}</StatusPill><button className="icon-button" type="button" title="Every seat is an indexed SeatFilled event"><Eye size={17} /></button></div></div>
              {slotView ? (
                <ProjectedSlotPanel slot={slotView} onChain selectedCycle={cycle} onSelectCycle={setCycle} columns={6} />
              ) : (
                <MatrixGraph nodes={nodes} rootId={member.id} cycle={cycle} />
              )}
            </motion.section>
          </AnimatePresence>

          <section className="matrix-stats four">
            <article className="panel"><span><Layers3 /></span><div><small>SEATS FILLED</small><strong>{selectedCycle ? `${selectedCycle.completedPositions} / ${selectedCycle.totalPositions}` : '0 / 12'}</strong></div></article>
            <article className="panel"><span><CircleDollarSign /></span><div><small>SEAT EVENTS</small><strong>{placements.length} on-chain</strong></div></article>
            <article className="panel"><span><Route /></span><div><small>DIRECT / ROUTED</small><strong>{directSeats} / {placements.length - directSeats}</strong></div></article>
            <article className="panel"><span><RefreshCw /></span><div><small>CYCLE STATE</small><strong>{selectedCycle?.status ?? 'Unavailable'}</strong></div></article>
          </section>

          <section className="detail-columns">
            <article className="panel routing-ledger">
              <div className="panel-heading"><div><span className="eyebrow">POSITION PROVENANCE</span><h2>Routing ledger</h2></div><Link to="/transactions" className="text-link">Full audit trail <ExternalLink size={13} /></Link></div>
              <div className="ledger-head"><span>Position</span><span>Seat</span><span>Occupant</span><span>Amount</span><span>Tx</span></div>
              {nodes.map((node) => {
                const placement = placements.find((item) => item.positionNumber === node.position);
                return <div className="ledger-row" key={node.position}><span>P{node.position.toString().padStart(2, '0')}</span><span><i className={`tiny-kind kind-${node.kind}`} />{placement ? (placement.kind ? POSITION_KIND_LABEL[placement.kind] ?? placement.kind : placement.placementType) : 'Open'}</span><span>{node.memberId ?? '—'}</span><strong>{placement ? formatToken(atomicToDecimal(placement.amountAtomic)) : '—'}</strong>{placement?.txHash ? <a href={`${EXPLORER_URL}/tx/${placement.txHash}`} target="_blank" rel="noreferrer" aria-label={`Transaction of P${node.position}`}><ExternalLink size={13} /></a> : <StatusPill tone="neutral">Open</StatusPill>}</div>;
              })}
            </article>
            <article className="panel cycle-timeline">
              <span className="eyebrow">CYCLE HISTORY</span><h2>Lifecycle</h2>
              {slotCycles.map((item) => <button key={item.cycleNumber} type="button" className={item.cycleNumber === cycle ? 'active' : ''} onClick={() => setCycle(item.cycleNumber)}><span>{item.status === 'completed' ? <ShieldCheck /> : <Clock3 />}</span><div><strong>Cycle {item.cycleNumber}</strong><small>{item.status} · {item.completedPositions} of {item.totalPositions} on-chain</small></div><ChevronRight /></button>)}
              {slotCycles.length === 0 ? <p className="empty-copy">No cycle has been indexed for this slot yet.</p> : null}
              <div className="timeline-note"><Sparkles /><p>Cycle history is immutable. Changing the view never changes your active state.</p></div>
            </article>
          </section>
          <div className="spec-callout compact"><Info /><div><strong>Matrix semantics</strong><p>Every seat is the matrix contract&apos;s SeatFilled event (a Treasury fallback entry is shown next to the routed seat, never as an occupant). What reached your wallet is in the income card on the overview (vault RewardPaid / Claimed).</p></div></div>
        </>
      )}
    </>
  );
}
